import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import axios from "axios";
import { devLog } from "../lib/devLog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
      return data;
    } catch (err) {
      devLog("Auth refresh failed (not logged in):", err?.response?.status);
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // Restore Authorization header fallback before checking the session.
    // Primary auth is the httpOnly cookie; localStorage token only covers
    // browsers with strict third-party/SameSite cookie policies.
    const t = localStorage.getItem("bc_admin_token");
    if (t) axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    if (data?.token) {
      localStorage.setItem("bc_admin_token", data.token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    }
    setUser({ id: data.id, email: data.email, role: data.role });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, null, { withCredentials: true });
    } catch (err) {
      devLog("Logout request failed:", err);
    }
    localStorage.removeItem("bc_admin_token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
